<template>
  <div
    v-if="!hasComportamentosAssociados"
    style="width: 100%"
  >
    <v-row>
      <v-col>
        <app-alert
          icon="info"
          message="Não há comportamentos associados cadastrados."
          type="info"
        />
      </v-col>
    </v-row>
  </div>
  <div
    v-else
    style="width: 100%"
  >
    <!-- bar chart -->
    <v-row id="grafico-barras" no-gutters>
      <v-col>
        <bar-chart
          :categorias="dadosBarchart.categorias"
          :dados="dadosBarchart.dados"
          :fonte="`Fonte: Sistema Desenvolva, ${anoMapeamento}`"
          :tooltip="dadosBarchart.tooltip"
          class="chart"
          data-type="integer"
          descricao-eixo-y="Comportamentos Associados"
          titulo="Monitoramento Geral de Competências Comportamentais"
        />
      </v-col>
    </v-row>

    <!-- pie chart -->
    <v-row id="grafico-pizza" no-gutters>
      <v-col>
        <pie-chart
          v-show="visualizacaoCompetencias === 'associados'"
          :dados="dadosPiechartAssociados.dados"
          :dados-complementares="dadosPiechartAssociados.dadosComplementares"
          :fonte="`Fonte: Sistema Desenvolva, ${anoMapeamento}`"
          class="chart"
          titulo="Comparativo de Competências Comportamentais"
        />
        <pie-chart
          v-if="hasComportamentosConsolidados"
          v-show="visualizacaoCompetencias === 'consolidados'"
          :dados="dadosPiechartConsolidados.dados"
          :dados-complementares="dadosPiechartConsolidados.dadosComplementares"
          :fonte="`Fonte: Sistema Desenvolva, ${anoMapeamento}`"
          class="chart"
          titulo="Comparativo de Competências Comportamentais"
        />
        <app-alert
          v-else
          icon="info"
          message="Não há comportamentos associados cadastrados."
          type="info"
        />
      </v-col>
    </v-row>

    <v-row class="justify-center">
      <v-radio-group
        v-model="visualizacaoCompetencias"
        row
      >
        <v-radio
          label="Associados"
          value="associados"
        />
        <v-radio
          label="Consolidados"
          value="consolidados"
        />
      </v-radio-group>
    </v-row>

    <!-- Resultados -->
    <v-row>
      <v-toolbar flat>
        <v-toolbar-title>
          <h2 class="title primary--text text--darken-1 text-no-wrap mt-0">
            RESULTADOS
          </h2>
        </v-toolbar-title>
      </v-toolbar>
    </v-row>
    <!-- ordenar por -->
    <v-row>
      <v-col>
        <app-sort v-model="opcoes" />
      </v-col>

      <v-spacer />

      <!-- filtrar por -->
      <v-col>
        <app-search v-model="opcoes" />
      </v-col>
    </v-row>

    <!-- tabela -->
    <v-row>
      <v-col>
        <v-data-table
          :headers="dadosTabela.headers"
          :items="dadosTabela.items"
          :search="opcoes.pesquisa"
          :sort-by="opcoes.ordenarPor"
          :sort-desc="opcoes.decrescente"
          align-content-space-between
          class="elevation-0"
          disable-pagination
          hide-default-footer
        >
          <template v-slot:[`item.unidade`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ item.unidade.sigla }}</span>
              </template>
              <span>{{ item.unidade.nome }}</span>
            </v-tooltip>
          </template>

          <template v-slot:[`body.append`]>
            <tr>
              <td>
                <strong
                  class="subtitle-2 grey--text text--darken-2"
                >Total de Comportamentos Associados</strong>
              </td>
              <td
                v-for="(c, ix) in dadosTabela.totalAssociados"
                :key="`${c}_${ix}`"
              >
                <strong>{{ c }}</strong>
              </td>
            </tr>
            <tr>
              <td>
                <strong
                  class="subtitle-2 grey--text text--darken-2"
                >Total de Comportamentos Consolidados</strong>
              </td>
              <td
                v-for="(c, ix) in dadosTabela.totalConsolidados"
                :key="`${c}_${ix}`"
              >
                <strong>{{ c }}</strong>
              </td>
            </tr>
            <v-divider />
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- lista de não comportamentos -->
    <v-row>
      <v-toolbar flat>
        <v-toolbar-title>
          <h2 class="title primary--text text--darken-1 text-no-wrap mt-0">
            LISTA DE NÃO-COMPORTAMENTOS (CATEGORIA "OUTROS") ({{
              naoComportamentos.length
            }})
          </h2>
        </v-toolbar-title>
      </v-toolbar>
    </v-row>
    <v-row>
      <v-col>
        <ul>
          <li
            v-for="(c, ix) in naoComportamentosOrdenados"
            :key="`${c}_${ix}`"
          >
            {{ c.descricaoAtualizada }}
          </li>
        </ul>
      </v-col>
    </v-row>
    <!-- Relatório PDF -->
    <v-row
      class="justify-end mb-1"
    >
      <v-tooltip
        max-width="400"
        top
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="success"
            @click="getRelatorio()"
            v-on="on"
          >
            <v-icon>get_app</v-icon>
            BAIXAR PDF
          </v-btn>
        </template>
        <span>Clique para baixar o relatório dos dados em formato PDF.</span>
      </v-tooltip>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import BarChart from '@/components/chart/BarChart';
import PieChart from '@/components/chart/PieChart';
import AppAlert from '@/components/core/AppAlert';
import AppSort from '@/components/core/AppSort';
import AppSearch from '@/components/core/AppSearch';
import { generatePDF, getCanvas } from '@/modules/relatorioPDF';

const ID_COMPETENCIA_OUTROS = 62;

export default {
  name: 'MonitoramentoGeralCompetenciaComportamental',

  components: {
    BarChart,
    PieChart,
    AppAlert,
    AppSort,
    AppSearch,
  },

  props: {
    mapeamento: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      opcoes: {
        pesquisa: '',
        decrescente: false,
        ordenarPor: 'unidade',
        selecionado: { id: 'unidade', descricao: 'Sigla da Unidade' },
        lista: [
          { id: 'unidade', descricao: 'Sigla da Unidade' },
          { id: 'total', descricao: 'Total de Comportamentos' },
        ],
      },
      visualizacaoCompetencias: 'associados',
    };
  },

  computed: {
    ...mapState({
      todasCompetenciasComportamentais: (state) => state.competencia.competenciasComportamentais,
      competenciasComportamentalAssociadosEQtdComportamento:
        (state) => state.monitoramento.mapeamento.competenciasComportamentais.competencias,
      competenciasComportamentalConsolidadosEQtdComportamento:
        (state) => state.monitoramento.mapeamento.competenciasComportamentaisConsolidadas.competencias,
      dadosUnidades: (state) => state.monitoramento.mapeamento.dadosUnidades,
      naoComportamentos: (state) => state.monitoramento.mapeamento.naoComportamentos,
    }),
    hasComportamentosAssociados() {
      return this.competenciasComportamentalAssociadosEQtdComportamento.reduce((t, c) => t + c.total, 0) > 0;
    },
    hasComportamentosConsolidados() {
      return this.competenciasComportamentalConsolidadosEQtdComportamento.reduce((t, c) => t + c.total, 0) > 0;
    },
    dadosTabela() {
      return {
        headers: [
          { text: 'UNIDADE', value: 'unidade', sortable: false },
          ...this.competenciasComportamentaisOrdenadas.map((c) => ({
            text: c.nome,
            value: `${c.id}`,
            sortable: false,
          })),
          { text: 'Total de Comportamentos', value: 'total', sortable: false },
        ],
        items: this.dadosUnidades
          .slice()
          .sort((u1, u2) => u1.unidade.nome.localeCompare(u2.unidade.nome))
          .map((u) => ({
            unidade: u.unidade,
            ...Object.fromEntries(
              this.competenciasComportamentaisOrdenadas.map((c) => [
                `${c.id}`,
                (
                  u.competenciasComportamentais.competencias.find((uc) => uc.competencia.nome === c.nome) || {
                    total: 0,
                  }
                ).total,
              ]),
            ),
            total: u.competenciasComportamentais.competencias.reduce((t, c) => t + c.total, 0),
          })),
        itemsPDF: this.dadosUnidades.slice().sort((u1, u2) => u1.unidade.nome.localeCompare(u2.unidade.nome))
          .map((u) => (
            [
              u.unidade.sigla,
              ...this.competenciasComportamentaisOrdenadas.map((c) => (
                (
                  u.competenciasComportamentais.competencias.find((uc) => uc.competencia.nome === c.nome) || 0
                ).total || 0
              )),
              u.competenciasComportamentais.competencias.reduce((t, c) => t + c.total, 0),
            ]
          )),
        totalAssociados: [
          ...this.competenciasComportamentaisOrdenadas.map((c) => this.dadosUnidades
            .map(
              (u) => u.competenciasComportamentais.competencias.find((uc) => uc.competencia.nome === c.nome) || {
                total: 0,
              },
            )
            .reduce((t, c2) => t + c2.total, 0)),
          this.dadosUnidades
            .flatMap((u) => u.competenciasComportamentais.competencias)
            .reduce((t, c) => t + c.total, 0),
        ],
        totalConsolidados: [
          ...this.competenciasComportamentaisOrdenadas.map((c) => this.dadosUnidades
            .map(
              (u) => u.competenciasComportamentaisConsolidadas.competencias.find(
                (uc) => uc.competencia.nome === c.nome,
              ) || { total: 0 },
            )
            .reduce((t, c2) => t + c2.total, 0)),
          this.dadosUnidades
            .flatMap((u) => u.competenciasComportamentaisConsolidadas.competencias)
            .reduce((t, c) => t + c.total, 0),
        ],
      };
    },
    anoMapeamento() {
      return this.mapeamento.ano ? this.mapeamento.ano : '';
    },
    naoComportamentosOrdenados() {
      return this.naoComportamentos.slice().sort((a, b) => a.descricaoAtualizada.localeCompare(b.descricaoAtualizada));
    },
    competenciaComportamentalOUTROS() {
      return this.todasCompetenciasComportamentais.find((c) => c.id === ID_COMPETENCIA_OUTROS);
    },
    competenciasComportamentaisOrdenadas() {
      const todasOrdenadas = this.todasCompetenciasComportamentais
        .slice()
        .filter((c) => c.id !== ID_COMPETENCIA_OUTROS)
        .sort((a, b) => a.nome.localeCompare(b.nome));

      return [...todasOrdenadas, this.competenciaComportamentalOUTROS];
    },
    mCompetenciaAssociadoQtdComportamento() {
      return this.competenciasComportamentalAssociadosEQtdComportamento.reduce((m, item) => {
        m.set(item.competencia.nome, item.total);
        return m;
      }, new Map());
    },
    mCompetenciaConsolidadoQtdComportamento() {
      return this.competenciasComportamentalConsolidadosEQtdComportamento.reduce((m, item) => {
        m.set(item.competencia.nome, item.total);
        return m;
      }, new Map());
    },
    dadosBarchart() {
      return {
        dados: [
          {
            name: 'Competências',
            data: this.competenciasComportamentaisOrdenadas.map(
              (c) => this.mCompetenciaAssociadoQtdComportamento.get(c.nome) || 0,
            ),
            color: '#2D5269',
          },
        ],
        categorias: this.competenciasComportamentaisOrdenadas.map((c) => c.nome),
        tooltip: {
          headerFormat: '<span style="padding:2px"><b>{point.key}</b></span><table>',
          pointFormat:
            '<tr>'
            + '<td style="color:{series.color};padding-top:5px;">Comportamentos Associados: </td>'
            + '<td style="padding-top:5px"><span>{point.y}</span></td>'
            + '</tr>',
          footerFormat: '</table>',
        },
      };
    },
    dadosPiechartAssociados() {
      return {
        dados: [
          {
            name: 'Competências',
            data: _.sortBy(
              this.competenciasComportamentaisOrdenadas.map((cc) => ({
                name: cc.nome,
                y: this.mCompetenciaAssociadoQtdComportamento.get(cc.nome) || 0,
                drilldown: this.mCompetenciaAssociadoQtdComportamento.get(cc.nome) ? cc.nome : null,
              })),
              (d) => -1 * d.y,
            ),
          },
        ],
        dadosComplementares: this.competenciasComportamentaisOrdenadas.map((c) => ({
          name: c.nome,
          id: c.nome,
          data: _.sortBy(
            this.dadosUnidades
              .filter((u) => u.competenciasComportamentais.competencias.find((uc) => uc.competencia.nome === c.nome))
              .map((u) => [
                u.unidade.sigla,
                u.competenciasComportamentais.competencias.find((uc) => uc.competencia.nome === c.nome).total,
              ]),
            (e) => -1 * e[1],
          ),
        })),
      };
    },
    dadosPiechartConsolidados() {
      return {
        dados: [
          {
            name: 'Competências',
            data: _.sortBy(
              this.competenciasComportamentaisOrdenadas.map((cc) => ({
                name: cc.nome,
                y: this.mCompetenciaConsolidadoQtdComportamento.get(cc.nome) || 0,
                drilldown: this.mCompetenciaConsolidadoQtdComportamento.get(cc.nome) ? cc.nome : null,
              })),
              (d) => -1 * d.y,
            ),
          },
        ],
        dadosComplementares: this.competenciasComportamentaisOrdenadas.map((c) => ({
          name: c.nome,
          id: c.nome,
          data: _.sortBy(
            this.dadosUnidades
              .filter((u) => u.competenciasComportamentaisConsolidadas.competencias
                .find((uc) => uc.competencia.nome === c.nome))
              .map((u) => [
                u.unidade.sigla,
                u.competenciasComportamentaisConsolidadas.competencias.find((uc) => uc.competencia.nome === c.nome)
                  .total,
              ]),
            (e) => -1 * e[1],
          ),
        })),
      };
    },
  },

  methods: {
    async getRelatorio() {
      const documento = {
        nome: 'relatorio-monitoramento-mapeamento-geral-competencia-comportamental',
        orientacao: 'landscape',
        cabecalho: {
          titulo: 'Relatório do Mapeamento Comportamental Geral',
          itens: [
            'Tipo de monitoramento: Competências Comportamentais',
            `Mapeamento: ${this.mapeamento.nome}`,
          ],
        },
        blocos: [
          {
            titulo: 'GRÁFICOS',
            subtitulo: 'MONITORAMENTO GERAL DE COMPETÊNCIAS COMPORTAMENTAIS',
            itens: [
              { tipo: 'imagem', valor: await getCanvas('#grafico-barras') },
            ],
          },
          {
            titulo: '',
            subtitulo: 'COMPARATIVO DE COMPETÊNCIAS COMPORTAMENTAIS',
            itens: [
              { tipo: 'imagem', valor: await getCanvas('#grafico-pizza') },
            ],
          },
          {
            titulo: 'RESULTADOS',
            subtitulo: '',
            itens: [{
              tipo: 'tabela',
              valor: {
                colunas: [
                  ...this.dadosTabela.headers.map((h) => (
                    h.text
                  )),
                ],
                linhas: this.dadosTabela.itemsPDF,
                rodape: [
                  ['Total de Comportamentos Associados', ...this.dadosTabela.totalAssociados],
                  ['Total de Comportamentos Consolidados', ...this.dadosTabela.totalConsolidados],
                ],
              },
            }],
          },
          this.naoComportamentos.length > 0 ? {
            titulo: `LISTA DE NÃO-COMPORTAMENTOS (CATEGORIA "OUTROS") (${this.naoComportamentos.length})`,
            subtitulo: '',
            itens: [
              {
                tipo: 'texto',
                valor: this.naoComportamentosOrdenados.map((c) => (
                  `• ${c.descricaoAtualizada}`
                )).join('\r\n'),
              },
            ],
          } : {},
        ],
      };
      generatePDF(documento);
    },
  },

};
</script>

<style scoped>
.chart {
  width: 100%;
  max-height: 400px;
  font-family: Roboto, sans-serif;
}

.chart >>> .highcharts-legend-item rect {
  display: none;
}

.chart >>> .highcharts-legend-item {
  pointer-events: none;
}
</style>
